<template>
  <StackPageLayout @close="handleClickClose">
    <template #header>
      <PageTitle>Transaction Details</PageTitle>
    </template>
    <QueryLoading v-if="isOrderLoading" />
    <QueryError v-else-if="isOrderError && order == null">
      Failed to retrieve order
    </QueryError>
    <template v-else-if="order">
      <section
        v-if="!order.invoice || !order.invoice.isPaid"
        class="mb-8"
      >
        <div
          class="rounded-lg border border-error bg-error/10 px-6 py-3 text-center font-semibold text-error"
        >
          Payment Incomplete
        </div>
      </section>
      <section class="mb-8">
        <div class="rounded-lg bg-background p-4">
          <div class="flex justify-between">
            <div>
              <dt class="mb-1 text-background-fg/70"
                >Total</dt
              >
              <dd class="text-3xl font-bold leading-none">
                {{ formattedTotal }}
              </dd>
            </div>
            <div
              v-if="gratuityPaid"
              class="flex flex-col justify-end text-sm text-background-fg/60"
            >
              (+{{
                formatToMerchantCurrencyString(gratuityPaid)
              }}
              gratuity)
            </div>
          </div>
          <div class="my-3 h-px bg-background-fg/8" />
          <div class="space-y-1">
            <StatRow
              v-if="order.orderNumber"
              label="Order No."
              :value="`#${order.orderNumber}`"
            />
            <StatRow
              v-if="order.serviceChargeTotal"
              label="Service Charge"
              :value="
                formatToMerchantCurrencyString(
                  order.serviceChargeTotal
                )
              "
            />
            <StatRow
              label="Date"
              :value="
                order.invoice?.paidAt ?? order.createdAt
              "
              type="date"
            />
            <StatRow
              label="Time"
              :value="
                order.invoice?.paidAt ?? order.createdAt
              "
              type="time"
            />
            <StatRow
              v-if="cashier"
              label="Cashier"
              :value="cashier.username"
            />

            <StatRow
              v-if="order.note"
              label="Note"
              :value="order.note"
            />
          </div>
        </div>
      </section>

      <section v-if="order.invoice" class="mb-8">
        <MoreMenuSubsection>
          <MoreMenuItemAction
            label="Receipt"
            :description="receiptActionDescription"
            :icon="IconReceiptFilled"
            @click="showReceiptDrawer"
          />
        </MoreMenuSubsection>
      </section>

      <section class="mb-8">
        <h3 class="mb-3 text-sm text-background-fg/70"
          >Payments
        </h3>
        <div class="space-y-2">
          <template
            v-for="transaction in order.payments"
            :key="transaction.id"
          >
            <TransactionItem
              :transaction="transaction"
              @click-transaction="onClickTransaction"
            />
          </template>
        </div>
        <div
          v-if="hasCurrencyMismatches"
          class="mt-4 rounded-lg border-l-4 border-amber-400 bg-amber-50 p-4 text-sm text-amber-800"
        >
          <p class="mb-1 font-semibold"
            >Note on highlighted transactions & refunds:</p
          >
          <p>
            These entries were made in a currency other than
            <strong>{{ merchantCurrency }}</strong
            >. They are recorded in their original currency
            but will <strong>not</strong> appear in any app
            or portal reporting.
          </p>
        </div>
      </section>

      <section>
        <h3 class="mb-3 text-sm text-background-fg/70">
          Items
        </h3>
        <div class="rounded-lg bg-background p-4">
          <ul class="space-y-2">
            <template
              v-for="orderItem in order.orderItems"
              :key="orderItem.id"
            >
              <OrderDetailItem :order-item="orderItem" />
            </template>
          </ul>
        </div>
      </section>
    </template>
  </StackPageLayout>

  <OrderReceiptDrawer
    ref="receiptDrawerRef"
    :order-id="orderId"
  />
  <TransactionDrawer
    v-if="currentTransaction"
    :is-open="isTransactionDrawerOpen"
    :show-retry="!order?.invoice || !order?.invoice.isPaid"
    :transaction="currentTransaction"
    @close="handleCloseTransactionDrawer"
  />
</template>

<script setup lang="ts">
import { IconReceiptFilled } from "@/icons"
import {
  useOrder,
  type OrderDetailPayment,
  type OrderDetailRefund,
} from "@/services/orders/retrieveOrder"
import { useCashiers } from "@/services/cashiers/listCashiers"

const props = defineProps<{
  orderId: string
  previousPage?: string
}>()
const router = useRouter()
const handleClickClose = () =>
  router.push(props.previousPage || { name: "orderIndex" })

const {
  data: order,
  isError: isOrderError,
  isLoading: isOrderLoading,
} = useOrder(props.orderId)
const { getById: getCashierById } = useCashiers()
const cashier = computed(() =>
  order.value
    ? getCashierById(order.value.cashierId).value
    : null
)

const { formatToMerchantCurrencyString } = useCurrency()
const { currency: merchantCurrency } = useTenant()

const { printingSupported } = useDevice()
const gratuityPaid = computed(() =>
  order.value?.invoice
    ? order.value.invoice.amountBalancedGratuity
    : 0
)
const formattedTotal = computed(() =>
  order.value
    ? formatToMerchantCurrencyString(
        order.value.invoice?.amountGross ??
          order.value.totalCost
      )
    : ""
)

const hasCurrencyMismatches = computed(() => {
  if (!order.value) return false
  return order.value.payments.some(
    (tx) =>
      tx.currency !== merchantCurrency.value ||
      tx.refunds.some(
        (r) => r.currency !== merchantCurrency.value
      )
  )
})

const receiptDrawerRef = useTemplateRef("receiptDrawerRef")
const showReceiptDrawer = () =>
  receiptDrawerRef.value?.reveal()
const receiptActionDescription = computed(() =>
  printingSupported.value
    ? "Print or send receipt"
    : "Send receipt"
)

const currentTransaction = ref<
  OrderDetailPayment | OrderDetailRefund
>()
const isTransactionDrawerOpen = ref(false)
const showTransactionDrawer = () =>
  (isTransactionDrawerOpen.value = true)
const handleCloseTransactionDrawer = () =>
  (isTransactionDrawerOpen.value = false)
const onClickTransaction = (
  transaction: OrderDetailPayment | OrderDetailRefund
) => {
  if (["cancelled", "pending"].includes(transaction.status))
    return
  currentTransaction.value = transaction
  showTransactionDrawer()
}
</script>
